import { t } from '@app/i18n';
import { LoadingButton } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { useCanOperar } from '@app/auth/useCan';
import { useDiscoveryNow } from '@app/discovery';

/**
 * O texto vem de `common.discoveryNow.*`, não do namespace desta tela: são dois
 * componentes (este e o de Identidades, que T4 e T21 usam) pintando **o mesmo
 * botão**. Duas cópias do texto em dois catálogos derivam na primeira revisão de
 * copy que tocar só uma delas.
 *
 * **Discovery agora** (FR-039, FR-040) — pede um tique da varredura e responde na
 * hora.
 *
 * Só admin/approver: `runDiscovery` é escrita, e FR-010 manda **omitir** o botão
 * de quem não pode — nunca desabilitar.
 *
 * O ciclo inteiro (disparo, acompanhamento a cada 3 s, tarefa no indicador, toast
 * de fecho e invalidação) mora em `useDiscoveryAgora`, compartilhado com T4, T21 e
 * a moldura. Aqui sobra o botão.
 *
 * O botão fica ocupado enquanto **houver varredura** — a deste clique ou a de
 * outra pessoa —, e não só enquanto o `POST` está em voo: o `POST` responde em
 * milissegundos e outro clique só ganharia um `409`. Quem conta os 20–40 s é o
 * indicador da topbar, sem travar a tela (29/08).
 */
export interface DiscoveryNowButtonProps {
  /** Recebe o erro do disparo, ou `null` quando ele começa bem. */
  onErro?: (erro: ApiError | null) => void;
  /**
   * Aviso da varredura **terminada** (`error_code` ou o primeiro `warnings[]`).
   * Desde 29/08 é por aqui que a falha do executor aparece: a varredura corre
   * fora da requisição e não tem mais como voltar num 502.
   */
  onAviso?: (aviso: string | null) => void;
}

export function DiscoveryNowButton({ onErro, onAviso }: DiscoveryNowButtonProps) {
  const canExecute = useCanOperar('runDiscovery');
  const discovery = useDiscoveryNow({ onErro, onAviso });

  if (!canExecute) return null;

  return (
    <LoadingButton
      variant="out"
      icon="refresh"
      loading={discovery.disparando || discovery.emExecucao}
      loadingLabel={t('common.discoveryNow.loading')}
      onClick={() => void discovery.executar()}
    >
      {t('common.discoveryNow.label')}
    </LoadingButton>
  );
}
